import React from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
import { useAuth } from '../../shared/hooks/useAuth';
import { ProtectedRoute } from '../../components/common/ProtectedRoute';
import { PublicRoute } from '../../components/common/PublicRoute';
import { RoleGuard } from '../../components/common/RoleGuard';
import { getUserRole } from '../../utils/roles';

// Layouts
import MainLayout from '../../components/layout/MainLayout';
import MinimalLayout from '../../components/layout/MinimalLayout';

// Auth Pages
const AuthLogin = React.lazy(() => import('../../features/auth/authentication/auth-forms/SimpleLogin'));
const AuthRegister = React.lazy(() => import('../../features/auth/authentication/auth-forms/AuthRegister'));
const AuthLoginOtp = React.lazy(() => import('../../features/auth/authentication/auth-forms/AuthOtp'));
const VerifyOTP = React.lazy(() => import('../../features/auth/authentication/auth-forms/VerifyOTP'));
const ForgotPassword = React.lazy(() => import('../../features/auth/authentication/auth-forms/ForgotPassword'));
const ResetPassword = React.lazy(() => import('../../features/auth/authentication/auth-forms/ResetPassword'));

// Dashboard
const DashboardDefault = React.lazy(() => import('../../features/dashboard/dashboard/Default'));

// Utilities
const UtilsTypography = React.lazy(() => import('../../components/common/utilities/Typography'));
const UtilsColor = React.lazy(() => import('../../components/common/utilities/Color'));
const UtilsShadow = React.lazy(() => import('../../components/common/utilities/Shadow'));
const UtilsMaterialIcons = React.lazy(() => import('../../components/common/utilities/MaterialIcons'));
const UtilsTablerIcons = React.lazy(() => import('../../components/common/utilities/TablerIcons'));

// Sample Page
const SamplePage = React.lazy(() => import('../../components/common/sample-page'));
const DBPoolManagement =React.lazy(()=>import('../../components/common/Db-pool-management'))
// Main Application Pages
const UserManagement = React.lazy(() => import('../../features/users/user'));
const ProductManagement = React.lazy(() => import('../../features/analytics/product'));
const ModuleManagement = React.lazy(() => import('../../features/analytics/module'));
const PlanManagement = React.lazy(() => import('../../features/analytics/plan'));
const SubscriptionManagement = React.lazy(() => import('../../features/subscriptions/subscription'));
const CustomerManagement = React.lazy(() => import('../../features/users/customer'));
const OrderManagement = React.lazy(() => import('../../features/payments/order'));
const Analytics = React.lazy(() => import('../../features/analytics/admin/SalesAnalytics'));

// Additional Management Pages
const DealerManagement = React.lazy(() => import('../../features/users/dealer'));
const ResellerManagement = React.lazy(() => import('../../features/users/reseller'));
const CampaignManagement = React.lazy(() => import('../../features/analytics/campaign/Campaign'));
const ConsultantManagement = React.lazy(() => import('../../features/users/consultant'));
const AssociationManagement = React.lazy(() => import('../../features/users/association'));
const IndustryManagement = React.lazy(() => import('../../features/analytics/industry-area'));
const ProspectManagement = React.lazy(() => import('../../features/analytics/prospect'));
const ActivityLogs = React.lazy(() => import('../../features/analytics/log'));
const SystemHealth = React.lazy(() => import('../../components/common/sample-page'));
const Settings = React.lazy(() => import('../../components/common/settings-page'));

// Profile & Payment Pages
const Profile = React.lazy(() => import('../../features/users/account-profile'));
const Payments = React.lazy(() => import('../../features/payments/payment/AddFunds'));
const NewOrder = React.lazy(() => import('../../features/payments/order'));
const OrderHistory = React.lazy(() => import('../../features/payments/order'));
const RenewSubscription = React.lazy(() => import('../../features/subscriptions/subscription'));

// Dealer Routes
const DealerAddFunds = React.lazy(() => import('../../features/users/dealer/AddFunds'));
const DealerCampaigns = React.lazy(() => import('../../features/analytics/campaign/Campaign'));

// Reseller Routes
const ResellerAddFunds = React.lazy(() => import('../../features/users/reseller/AddFunds'));
const ResellerCampaigns = React.lazy(() => import('../../features/analytics/campaign/Campaign'));

// Customer Routes
const CustomerAddFunds = React.lazy(() => import('../../features/users/customer/AddFunds'));

// Admin Routes
const SalesAnalytics = React.lazy(() => import('../../features/analytics/admin/SalesAnalytics'));

// Credits Management
const CreditsPage = React.lazy(() => import('../../features/credits/CreditsPage'));

// Loading Component
const LoadingSpinner = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}
  >
    <div>Loading...</div>
  </div>
);

// Role-based Redirect Component
// Redirects customer (role_id: 4) to subscriptions, others to dashboard
const RoleBasedRedirect = () => {
  const userRole = getUserRole();
  const targetRoute = userRole === 4 ? '/subscriptions' : '/dashboard';
  return <Navigate to={targetRoute} replace />;
};

// Route Components with Suspense
const withSuspense = (Component: React.ComponentType<any>) => (
  <React.Suspense fallback={<LoadingSpinner />}>
    <Component />
  </React.Suspense>
);

// Route Components with Suspense + Role Guard
const withGuard = (path: string, Component: React.ComponentType<any>) => (
  <RoleGuard path={path}>
    <React.Suspense fallback={<LoadingSpinner />}>
      <Component />
    </React.Suspense>
  </RoleGuard>
);

export default function AppRoutes() {
  const { userRole } = useAuth();

  const routes = [
    // Public routes (authentication)
    {
      path: '/',
      element: <PublicRoute />,
      children: [
        {
          element: <MinimalLayout />,
          children: [
            { path: '/', element: <Navigate to="/login" replace /> },
            { path: '/login', element: withSuspense(AuthLogin) },
            { path: '/register', element: withSuspense(AuthRegister) },
            { path: '/login-otp', element: withSuspense(AuthLoginOtp) },
            { path: '/verify-otp', element: withSuspense(VerifyOTP) },
            { path: '/forgot-password', element: withSuspense(ForgotPassword) },
            { path: '/reset-password', element: withSuspense(ResetPassword) }
          ]
        }
      ]
    },
    // Protected routes
    {
      path: '/',
      element: <ProtectedRoute />,
      children: [
        {
          path: '/',
          element: <MainLayout />,
          children: [
            // Main Application Pages - Role-guarded routes
            { path: '/users', element: withGuard('/users', UserManagement) },
            { path: '/distributors', element: withGuard('/distributors', DealerManagement) },
            { path: '/dealers', element: <Navigate to="/distributors" replace /> }, // Redirect old path
            { path: '/resellers', element: withGuard('/resellers', ResellerManagement) },
            { path: '/customers', element: withGuard('/customers', CustomerManagement) },
            { path: '/dashboard', element: withGuard('/dashboard', DashboardDefault) },
            { path: '/', element: <RoleBasedRedirect /> },
            { path: '/products', element: withGuard('/products', ProductManagement) },
            { path: '/modules', element: withGuard('/modules', ModuleManagement) },
            { path: '/plans', element: withGuard('/plans', PlanManagement) },
            { path: '/subscriptions', element: withSuspense(SubscriptionManagement) }, // All roles
            { path: '/orders', element: withSuspense(OrderManagement) }, // All roles
            { path: '/campaigns', element: withGuard('/campaigns', CampaignManagement) },
            { path: '/consultants', element: withGuard('/consultants', ConsultantManagement) },
            { path: '/associations', element: withGuard('/associations', AssociationManagement) },
            { path: '/industries', element: withGuard('/industries', IndustryManagement) },
            { path: '/prospects', element: withGuard('/prospects', ProspectManagement) },
            { path: '/activity-logs', element: withGuard('/activity-logs', ActivityLogs) },
            { path: '/system/health', element: withGuard('/system/health', SystemHealth) },
           
            { path: '/db-pools', element: withGuard('/db-pools', DBPoolManagement) },

            { path: '/settings', element: withSuspense(Settings) }, // All roles
            { path: '/analytics', element: withGuard('/analytics', Analytics) },
            
            // Credits Management - Owner + Distributor + Reseller only
            { path: '/credits', element: withGuard('/credits', CreditsPage) },
            
            // Profile & Payment Pages
            { path: '/profile', element: withSuspense(Profile) }, // All roles
            { path: '/payments', element: withGuard('/payments', Payments) },
            { path: '/orders/new', element: withSuspense(NewOrder) }, // All roles
            { path: '/orders/history', element: withSuspense(OrderHistory) }, // All roles
            { path: '/subscriptions/renew', element: withSuspense(RenewSubscription) }, // All roles
            
            // Legacy singular paths (redirect to plural)
            { path: '/user', element: <Navigate to="/users" replace /> },
            { path: '/product', element: <Navigate to="/products" replace /> },
            { path: '/plan', element: <Navigate to="/plans" replace /> },
            { path: '/subscription', element: <Navigate to="/subscriptions" replace /> },
            { path: '/customer', element: <Navigate to="/customers" replace /> },
            { path: '/order', element: <Navigate to="/orders" replace /> },

            // Utilities
            { path: '/utils/typography', element: withSuspense(UtilsTypography) },
            { path: '/utils/color', element: withSuspense(UtilsColor) },
            { path: '/utils/shadow', element: withSuspense(UtilsShadow) },
            { path: '/utils/icons/material', element: withSuspense(UtilsMaterialIcons) },
            { path: '/utils/icons/tabler', element: withSuspense(UtilsTablerIcons) },

            // Sample Page
            { path: '/sample-page', element: withSuspense(SamplePage) },

            // Role-based routes (with guards)
            ...(userRole === 2 || userRole === 1
              ? [
                  { path: '/distributor/add-funds', element: withGuard('/distributor/add-funds', DealerAddFunds) },
                  { path: '/distributor/campaigns', element: withGuard('/distributor/campaigns', DealerCampaigns) }
                ]
              : []),

            ...(userRole === 3 || userRole === 1
              ? [
                  { path: '/reseller/add-funds', element: withGuard('/reseller/add-funds', ResellerAddFunds) },
                  { path: '/reseller/campaigns', element: withGuard('/reseller/campaigns', ResellerCampaigns) }
                ]
              : []),

            ...(userRole === 4 || userRole === 1
              ? [{ path: '/customer/add-funds', element: withGuard('/customer/add-funds', CustomerAddFunds) }]
              : []),

            ...(userRole === 1 ? [{ path: '/admin/sales-analytics', element: withGuard('/admin/sales-analytics', SalesAnalytics) }] : []),

            // Catch all route - redirects based on role
            { path: '*', element: <RoleBasedRedirect /> }
          ]
        }
      ]
    }
  ];

  return useRoutes(routes);
}
